import {useCallback, useEffect, useMemo, useRef} from 'react';

import {TileMedia, TileMediaSpec, TileMediaTracking} from '../../MediaCard';
import {fetchNewTileMedia} from './fetch';
import IN_MEMORY_CACHE, {
  TileMediaStatus,
  updateTileMediaStatus,
} from './inMemoryCache';
import {getKeyForTileMediaSpec} from './keys';

export * from './augment';
export {fetchMaxStepWithAvailableMedia} from './fetch';
export * from './keys';
export * from './paths';

export type Dimensions =
  | {
      height: number;
    }
  | {width: number}
  | {height: number; width: number};

/**
 * Check for existing cached entry, which we only take if its step matches with the one specified in the spec.
 *
 * If the steps do not match, it means that we "rolled back" and displayed media from a previous step because we assumed that the media for the actual step hasn't finished uploading yet. Always refetch in case the existing entry is rolled back, as the real thing may be available now.
 */
function tileMediaSpecIsNew(tileMediaSpec: TileMediaSpec): boolean {
  const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);
  const existingStatus =
    IN_MEMORY_CACHE.TILE_MEDIA_STATUS_BY_KEY.get(tileMediaSpecKey);
  const existingTileMedia =
    IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.get(tileMediaSpecKey);

  // If we have data but the step doesn't match, we need to refetch
  if (existingTileMedia && existingTileMedia.step !== tileMediaSpec.mediaStep) {
    return true;
  }

  // If we've never requested this spec before, it's new
  if (!existingStatus || !existingStatus.isRequested) {
    return true;
  }

  if (existingStatus?.hasError) {
    return true;
  }

  // Otherwise, we've already requested this spec (successfully or with error), so it's not new
  return false;
}

export function isComplete(
  tileMediaStatus: TileMediaStatus | null | undefined
): boolean {
  return !!(
    tileMediaStatus &&
    (tileMediaStatus.isLoaded || tileMediaStatus.hasError)
  );
}

/**
 * This hook is how the Media Panel uses the fetch pipeline. It always returns the global cache, but takes care of loading new data as needed.
 *
 * The _essential complexity_ is: given an array of TileMediaSpecs, resolve the asset paths for each spec and return those URLs for use in the UI.
 * TileMediaSpec[] -> Map<TileMediaSpecKey, TileMedia>
 *
 * The _accidental complexity_ is:
 * 1. Multiple panels can load simultaneously with tile specs referencing assets in common— the effect being a lot of redundant fetching if previous individual fetches aren't reused
 * 2. The upload path for file assets lags the record being created in the database, so the UI can be left in a long-running "loading" state if the asset is fetched before it's ready, therefore there's a need to filter in-processing entries out of the results
 * 3. Two different ways of accessing the asset through a URL exist: the `directURL` (which is a signed URL to the underlying GCS bucket), and a `url` (which comes filtered through Gorilla). Historically the decision was to standardize on the `directURL`, but the need for dynamic image sizing as a performance measure means certain asset types (i.e. images) need to be redirected to the URL
 * 4. There are multiple asset types (images, video, audio, plotly, etc...) that can be returned from this pipeline. A decision to handle them all as blobs and access them with objectURLs was made to simplify the central abstraction, but this indirection exerts penalties on things like default browser caching strategies
 * 5. The pipeline fetching these assets was decoupled from the React render cycle to avoid render thrash. The assets are stored in-memory and render updates are triggered through a React hook that internally performs lookups on this in-memory cache. Some centralization of this state has been completed and is exposed via the IN_MEMORY_CACHE object, but other in-memory state exists within the pipeline.
 *
 * As experienced by a React component, the pipeline is as follows:
 * 1. Each panel calls the hook and provides a list of TileMediaSpecs.
 * 2. As the panels receive updated lists of TileMediaSpecs, the hook will rerun.
 * 3. The `useEffect` hook will check if any of the TileMediaSpecs are new (not in the global cache). If so, it flips the `loading` state to `true` and calls `update`
 * 4. The `getTileMediaKeyBy` function is a `useCallback` with a dependency on loading, so it will then refresh, triggering components to rerender and retrieve the new data from the global cache.
 */
export function useTileMedia(
  tileMediaSpec: TileMediaSpec,
  attemptNonexistentSteps: boolean
): {
  loading: boolean;
  tileMedia: TileMedia | null;
  status: TileMediaStatus;
  getTileMediaTracking: (
    tileMediaSpec: TileMediaSpec
  ) => TileMediaTracking | null;
} {
  const tileMediaSpecKey = useMemo(
    () => getKeyForTileMediaSpec(tileMediaSpec),
    [tileMediaSpec]
  );
  const attemptNonexistentStepsRef = useRef(attemptNonexistentSteps);

  useEffect(() => {
    attemptNonexistentStepsRef.current = attemptNonexistentSteps;
  }, [attemptNonexistentSteps]);

  const setStatus = useCallback(
    (newStatus: Partial<TileMediaStatus>) => {
      updateTileMediaStatus(tileMediaSpecKey, newStatus);
    },
    [tileMediaSpecKey]
  );

  const getTileMediaTracking = useCallback((tileMediaSpec: TileMediaSpec) => {
    const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);
    return (
      IN_MEMORY_CACHE.TILE_MEDIA_TRACKING_BY_KEY.get(tileMediaSpecKey) ?? null
    );
  }, []);

  const fetchData = useCallback(
    async (spec: TileMediaSpec) => {
      const status =
        IN_MEMORY_CACHE.TILE_MEDIA_STATUS_BY_KEY.get(tileMediaSpecKey);

      if (status?.isLoading || status?.isLoaded) {
        return;
      }

      setStatus({
        isLoading: true,
        isLoaded: false,
        isRequested: true,
        lastRequestedAt: Date.now(),
      });

      try {
        await fetchNewTileMedia([spec], attemptNonexistentStepsRef.current);
      } catch (error) {
        console.error('fetchData error', error);
      }
    },
    [tileMediaSpecKey, setStatus]
  );

  useEffect(() => {
    if (tileMediaSpecIsNew(tileMediaSpec)) {
      fetchData(tileMediaSpec);
    }
  }, [tileMediaSpec, fetchData]);

  const tileMedia =
    IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.get(tileMediaSpecKey) ?? null;
  const status = IN_MEMORY_CACHE.TILE_MEDIA_STATUS_BY_KEY.get(
    tileMediaSpecKey
  ) ?? {
    isLoading: false,
    isRequested: false,
    isLoaded: false,
    hasError: false,
    lastRequestedAt: 0,
    lastUpdatedAt: 0,
  };

  return {
    loading: !isComplete(status),
    tileMedia,
    status,
    getTileMediaTracking,
  };
}
